import { config } from '@lingoda/config';
import {
    devOmitSentryTunnel,
    getDevClassroomLogsApiKeyOverwrite,
    getDevClassroomLogsEndpointOverwrite,
    getDevClassroomSentryDsnOverwrite,
} from '@lingoda/local-feature-flags';
import { isLocalHost } from '@lingoda/urls';
import { HotjarCustomEvent, addHotjarCustomEvent, initGtm } from '@lingoda/analytics';
import { classroomDemoClassEntryPath, history } from '@lingoda/router';
import {
    initErrorTracker,
    initLogs,
    setLogContext,
    setMonitorTagOnce,
    setPlatformLogContext,
} from '@lingoda/monitor';
import { isZoomWorkerWasmNotReadyError } from './zoomWorkerErrors';

const sentryDsn = getDevClassroomSentryDsnOverwrite() ?? config.sentryFrontendDsn;
const sentryTunnelEndpoint = devOmitSentryTunnel() ? undefined : config.sentryTunnelEndpoint;
const shouldInitErrorTracker = !!sentryDsn && !isLocalHost();
const ERROR_NAMES_TO_IGNORE = [
    'Trying to generate Zoom session too late',
    'User is blocked for this class',
    // LW-35169 Zoom's whiteboard-token fetch on each join (unused here), 429 at class-start peaks.
    // ponytail: matches any RxJS ajax error (only Zoom makes them); filter by URL if that changes
    'AjaxError: ajax error',
];

if (shouldInitErrorTracker) {
    initErrorTracker(
        {
            ...config,
            sentryDsn,
            errorNamesToIgnore: ERROR_NAMES_TO_IGNORE,
            isLogOnlyWorkerError: isZoomWorkerWasmNotReadyError,
            sentryTunnelEndpoint,
        },
        history,
    );

    const classUid = getClassUid();
    if (classUid) {
        setMonitorTagOnce('classUid', classUid);
    }
}

const logEndpoint = getDevClassroomLogsEndpointOverwrite() ?? config.logEndpoint;
const logApiKey = getDevClassroomLogsApiKeyOverwrite() ?? config.logApiKey;
const shouldInitLogs = !!logEndpoint && !!logApiKey && !isLocalHost();
if (shouldInitLogs) {
    initLogs({
        endpoint: logEndpoint,
        apiKey: logApiKey,
        releaseId: String(config.releaseId),
        deployEnvironment: config.deployEnvironment,
    });
    setLogContext({ classUid: getClassUid() });
    setPlatformLogContext().catch(console.error);
}

function getClassUid(): string | undefined {
    // Parse for `/class/:classUid` matches
    const match = window.location.pathname.match(/\/class\/(\w+)/);
    if (!match) {
        return undefined;
    }

    return match[1];
}

window.addEventListener('load', () => {
    initGtm('classroomGoogleTagManagerId');

    if (!window.location?.pathname?.includes(classroomDemoClassEntryPath() as string)) {
        addHotjarCustomEvent(HotjarCustomEvent.RecordClass);
    }
});
